import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, User, Building, MessageSquare, MapPin, X, Bot, Send } from 'lucide-react';

// Define the Chatbot component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Initial welcome message
  useEffect(() => {
    setMessages([
      { role: "bot", text: "Hello! I'm your virtual assistant. How can I help you today?" }
    ]);
  }, []);

  const handleSendMessage = async () => {
    if (inputValue.trim() === "") return;

    const userMessage = { role: "user", text: inputValue };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      let chatHistory = messages.map(msg => ({ role: msg.role === 'bot' ? 'model' : msg.role, parts: [{ text: msg.text }] }));
      chatHistory.push({ role: "user", parts: [{ text: inputValue }] });
      
      const payload = { contents: chatHistory };
      const apiKey = "AIzaSyCdz16I4GxJMTsRxkdciyHlDnSQOJ2WkxI";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

      const fetchWithBackoff = async (url, options, retries = 3, delay = 1000) => {
        try {
          const response = await fetch(url, options);
          if (response.status === 429 && retries > 0) {
            console.warn(`Rate limit hit. Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return fetchWithBackoff(url, options, retries - 1, delay * 2);
          }
          return response;
        } catch (error) {
          if (retries > 0) {
            console.warn(`Fetch failed. Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return fetchWithBackoff(url, options, retries - 1, delay * 2);
          }
          throw error;
        }
      };

      const response = await fetchWithBackoff(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const botText = result.candidates[0].content.parts[0].text;
        const botMessage = { role: "bot", text: botText };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
      } else {
        const errorMessage = { role: "bot", text: "Sorry, I couldn't get a response. Please try again later." };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      const errorMessage = { role: "bot", text: "An error occurred while fetching the response." };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Toggle button */}
      {/* <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-full text-white shadow-xl hover:bg-red-700 transition duration-300 transform hover:scale-110"
        aria-label={isOpen ? "Close Chatbot" : "Open Chatbot"}
      >
        {isOpen ? <X size={24} /> : <Bot size={24} />}
      </button> */}

      {/* Chatbot window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 h-96 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden transform transition-all duration-300 origin-bottom-right">
          <div className="flex items-center justify-between p-4 bg-red-600 text-white shadow-md">
            <h3 className="text-lg font-bold">Astergo Chatbot</h3>
            <button onClick={() => setIsOpen(false)} aria-label="Close Chat">
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-xs p-3 rounded-xl shadow-sm ${
                    msg.role === "user"
                      ? "bg-red-500 text-white rounded-br-none"
                      : "bg-gray-200 text-gray-800 rounded-bl-none"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-xs p-3 rounded-xl rounded-bl-none shadow-sm bg-gray-200 text-gray-800">
                  <span className="animate-pulse">Typing...</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t border-gray-200 flex items-center">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="flex-1 py-2 px-4 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading}
              className="ml-2 w-10 h-10 flex items-center justify-center bg-red-600 text-white rounded-full hover:bg-red-700 transition duration-300 disabled:bg-red-400"
              aria-label="Send Message"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const ContactForm = () => {
  // Use a state hook to manage the form's data.
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyName: "",
    mobileNumber: "",
    requirements: "",
  });

  // This function updates the state whenever an input field changes.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // This is the function that will send the data to your backend API.
  const handleSubmit = async (e) => {
    // Prevent the default form submission behavior (page reload).
    e.preventDefault();

    // The URL of your .NET backend's contactus endpoint.
    // Use the http profile port from launchSettings.json (5108)
    // or the IIS Express port (58826) depending on how you run your backend.
    const apiUrl = "http://localhost:58826/api/contact/contactus";

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // If the response status is 200-299, it's a success.
        const result = await response.json();
        alert(result.message);
        // Clear the form fields after a successful submission.
        setFormData({
          fullName: "",
          email: "",
          companyName: "",
          mobileNumber: "",
          requirements: "",
        });
      } else {
        // If the response status is an error, parse and display it.
        const errorData = await response.json();
        const errorMessage = errorData.errors
          ? JSON.stringify(errorData.errors)
          : "An unknown error occurred.";
        alert(`Submission failed: ${errorMessage}`);
      }
    } catch (error) {
      // Catch any network or other errors.
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again later.");
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-100 to-white min-h-screen font-inter text-gray-900 flex flex-col justify-center items-center p-4">
      <div className="max-w-6xl mx-auto w-full py-12">
        {/* Modern white card container with 3D effect */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 lg:p-16 border border-gray-200 transform transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl">
          <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-800 mb-4 text-center">Let's Connect</h1>
          <p className="text-base lg:text-lg text-gray-600 mb-10 text-center max-w-2xl mx-auto">
            Ready to partner with us? Fill out the form or find our office on the map.
          </p>
          
          {/* Main content grid for form and contact info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
            {/* Form section */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name*"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 rounded-md py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                  required
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  name="email"
                  placeholder="Email*"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 rounded-md py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                  required
                />
              </div>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name*"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 rounded-md py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                  required
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="tel"
                  name="mobileNumber"
                  placeholder="Mobile Number"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 rounded-md py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                />
              </div>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-4 text-gray-400" size={20} />
                <textarea
                  name="requirements"
                  placeholder="Share your requirements*"
                  value={formData.requirements}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 rounded-md py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 resize-none h-32 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                  required
                ></textarea>
              </div>
              
              <div className="flex justify-center md:justify-start">
                <button
                  type="submit"
                  className="w-full md:w-auto bg-red-600 text-white font-semibold py-3 px-8 rounded-md shadow-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white transition duration-300 transform hover:scale-105"
                >
                  Send Message
                </button>
              </div>
            </form>

            {/* Address, Contact, and Map sections */}
            <div className="flex flex-col space-y-6 md:space-y-8">
              {/* New flex container for Address and Contact Details to place them on the same row */}
              <div className="flex flex-col md:flex-row md:space-x-8 space-y-6 md:space-y-0">
                {/* Address section */}
                <div className="space-y-4 w-full md:w-1/2">
                  <h2 className="text-2xl font-bold text-gray-800">Our Address</h2>
                  <div className="flex items-start text-gray-600">
                    <MapPin className="mt-1 mr-3 text-red-500 flex-shrink-0" size={20} />
                    <p className="flex-grow">
                      IITL Nimbus Express Parkview 2 , Yamuna Expy Sector Chi V ,
                      <br />
                      Greater Noida , Uttar Pradesh
                      <br />
                      India - 201301
                    </p>
                  </div>
                </div>
                
                {/* Contact Details section */}
                <div className="space-y-4 w-full md:w-1/2">
                  <h2 className="text-2xl font-bold text-gray-800">Contact Details</h2>
                  <div className="flex items-center text-gray-600">
                    <Phone className="mr-3 text-red-500 flex-shrink-0" size={20} />
                    <p>8210345655 / 9163091507</p>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Mail className="mr-3 text-red-500 flex-shrink-0" size={20} />
                    <p>info@astergo.in</p>
                  </div>
                </div>
              </div>

              {/* Google Map iframe */}
              <div className="w-full h-80 rounded-xl overflow-hidden shadow-2xl border border-gray-300">
                <iframe
                  title="Our Office Location"
                  src="https://maps.google.com/maps?q=23.3432927,86.3596211&hl=en&z=18&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Chatbot />
    </div>
  );
};

export default ContactForm;
